
import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Command, CommandEmpty, CommandGroup, CommandItem, CommandList } from "@/components/ui/command";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Check, ChevronsUpDown, X } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { cn } from "@/lib/utils";

const difficultyOptions = [
  { value: 'easy', label: 'Easy', color: 'bg-green-100 text-green-800' },
  { value: 'medium', label: 'Medium', color: 'bg-yellow-100 text-yellow-800' },
  { value: 'hard', label: 'Hard', color: 'bg-red-100 text-red-800' }
];

const DifficultyDropdown = ({ 
  selectedDifficulties = [], 
  onSelectionChange, 
  placeholder = "Select Difficulties...",
  className,
  disabled = false,
  multiple = true
}) => {
  const [open, setOpen] = useState(false);

  const handleSelect = (difficulty) => {
    if (multiple) {
      const updatedSelection = selectedDifficulties.includes(difficulty)
        ? selectedDifficulties.filter(d => d !== difficulty)
        : [...selectedDifficulties, difficulty];
      onSelectionChange(updatedSelection);
    } else {
      onSelectionChange(selectedDifficulties.includes(difficulty) ? [] : [difficulty]);
      setOpen(false);
    }
  };

  const clearSelection = (e) => {
    e.stopPropagation();
    onSelectionChange([]);
  };

  const getSelectedOptions = () => {
    return difficultyOptions.filter(option => selectedDifficulties.includes(option.value));
  };

  const selectedOptions = getSelectedOptions();

  return (
    <div className={className}>
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button
            variant="outline"
            role="combobox"
            aria-expanded={open}
            className="w-full justify-between min-h-10"
            disabled={disabled}
          >
            <div className="flex flex-wrap gap-1 max-w-full">
              {selectedOptions.length === 0 ? (
                <span className="text-muted-foreground">{placeholder}</span>
              ) : (
                selectedOptions.map((option) => (
                  <Badge 
                    key={option.value} 
                    variant="secondary" 
                    className={`text-xs ${option.color}`}
                  >
                    {option.label}
                  </Badge>
                ))
              )}
            </div>
            <div className="flex items-center gap-1">
              {selectedOptions.length > 0 && (
                <X 
                  className="h-4 w-4 shrink-0 opacity-50 hover:opacity-100" 
                  onClick={clearSelection}
                />
              )}
              <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
            </div>
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-full p-0" style={{ width: 'var(--radix-popover-trigger-width)' }}>
          <Command>
            <CommandList>
              <CommandEmpty>No difficulties found.</CommandEmpty>
              <CommandGroup>
                {difficultyOptions.map((option) => (
                  <CommandItem
                    key={option.value}
                    value={option.value}
                    onSelect={() => handleSelect(option.value)}
                  >
                    <Check
                      className={cn(
                        "mr-2 h-4 w-4",
                        selectedDifficulties.includes(option.value) ? "opacity-100" : "opacity-0"
                      )}
                    />
                    <Badge 
                      variant="outline" 
                      className={`${option.color} border-0`}
                    >
                      {option.label}
                    </Badge>
                  </CommandItem>
                ))}
              </CommandGroup>
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>
    </div>
  );
};

export default DifficultyDropdown;
